import {
  checkIsMastraErrorToolOutput,
  type MastraErrorToolOutput,
} from "@repo/app-types/agents/mastraErrorToolOutput";
import type {
  SimulateRolePlayRealismToolInput,
  SimulateRolePlayRealismToolOutput,
} from "@repo/app-types/entities/trainingConfigurationTools";
import { ChatToolBase, type ToolComponent } from "@repo/ui/chat/chatToolCall";
import { IconChevronRight, IconMessages } from "@tabler/icons-react";
import type { FC } from "react";
import {
  Button,
  Disclosure,
  DisclosurePanel,
  Heading,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";

export const SimulateRolePlayRealismTool: ToolComponent<
  SimulateRolePlayRealismToolInput,
  SimulateRolePlayRealismToolOutput | MastraErrorToolOutput
> = (props) => {
  if (props.status.type === "running") {
    return (
      <ChatToolBase status={props.status}>
        Testing role-play realism...
      </ChatToolBase>
    );
  }

  if (checkIsMastraErrorToolOutput(props.output)) {
    return (
      <ChatToolBase status={props.status}>
        An error occurred while testing role-play realism
      </ChatToolBase>
    );
  }

  if (
    props.status.type === "complete" &&
    props.output &&
    Array.isArray(props.output.transcript)
  ) {
    return (
      <Disclosure className="group pl-3">
        <Heading>
          <Button
            slot="trigger"
            className="flex w-full items-start gap-1.5 text-left text-xs font-medium text-muted-foreground hover:text-gray-700"
          >
            <IconChevronRight className="mt-0.5 size-3.5 shrink-0 transition-transform duration-200 group-data-[expanded]:rotate-90" />
            <IconMessages className="mt-0.5 size-3.5 shrink-0 text-indigo-500" />
            <span className="line-clamp-3">Simulated role-play transcript</span>
          </Button>
        </Heading>
        <DisclosurePanel>
          <TranscriptDetails result={props.output} />
        </DisclosurePanel>
      </Disclosure>
    );
  }

  // Completed but the payload is missing or not a transcript (e.g. an aborted
  // run or an old persisted output shape) — don't leave a permanent
  // "testing..." spinner in the chat history.
  if (props.status.type === "complete") {
    return (
      <ChatToolBase status={props.status}>
        No role-play simulation result was returned.
      </ChatToolBase>
    );
  }

  return (
    <ChatToolBase status={props.status}>
      Testing role-play realism...
    </ChatToolBase>
  );
};

const TranscriptDetails: FC<{ result: SimulateRolePlayRealismToolOutput }> = ({
  result,
}) => (
  <div className="mt-2 space-y-2 border-l-2 border-border-light pl-3 text-xs text-gray-600">
    {result.transcript.length > 0 ? (
      <section>
        <div className="space-y-1">
          {result.transcript.map((message, index) => (
            <div
              key={index}
              className={twMerge(
                "rounded-lg px-2.5 py-1.5 whitespace-pre-wrap",
                message.role === "participant" &&
                  "bg-indigo-50 text-indigo-900",
                message.role === "role_play_agent" &&
                  "bg-gray-100 text-foreground",
              )}
            >
              {message.content}
            </div>
          ))}
        </div>
      </section>
    ) : null}
    {result.assessment ? (
      <section className="space-y-2">
        <h4 className="font-medium text-gray-700">Realism assessment</h4>
        <p className="whitespace-pre-wrap">{result.assessment.summary}</p>
        {result.assessment.findings?.length ? (
          <div className="space-y-1">
            <h5 className="font-medium text-gray-700">Findings</h5>
            {result.assessment.findings.map((finding, index) => (
              <p
                key={index}
                className="rounded-lg bg-gray-50 px-2.5 py-1.5 whitespace-pre-wrap"
              >
                {finding.commentary}
              </p>
            ))}
          </div>
        ) : null}
      </section>
    ) : null}
  </div>
);
